import { createContext, useContext } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';

import EquipmentStatus from '@models/EquipmentStatus.model';

import { State, useState, createState } from '@hookstate/core';

import { getFilesByStudio } from '@services/queries/onboarding/getFilesByStudio';
import { NavigationContext } from '@layout/navigation/navigation';
import {
  getFilesCount,
  IGetFilesCount,
} from '@services/queries/onboarding/getFilesCount';
import { getSearchFiles } from '@services/queries/onboarding/getSearchFiles';
import { searchFiles } from '@services/mixpanel/trackers/Onboarding';
import { OnboardingRaw } from './Onboarding.raw';

export type SortOnboardingTypes =
  | 'equipment_status.name'
  | 'equipment_status.description'
  | 'equipment_status.category'
  | undefined;

export type DirectionOnboardingtypes = 'ASC' | 'DESC' | undefined;

interface IOnboardingContext {
  equipmentStatus: EquipmentStatus;
  openDelete: State<boolean>;
  search: State<string>;
  sort: State<SortOnboardingTypes>;
  direction: State<DirectionOnboardingtypes>;
  selectedOnboardingFile: State<string>;
  openImportInventory: State<boolean>;
  openImportInvoice: State<boolean>;
  openQuickAdd: State<boolean>;
  filesCount?: IGetFilesCount;
}

export const OnboardingContext = createContext<IOnboardingContext>({
  equipmentStatus: EquipmentStatus.createEmpty(),
  openDelete: createState<boolean>(false),
  search: createState<string>(''),
  sort: createState<SortOnboardingTypes>(undefined),
  direction: createState<DirectionOnboardingtypes>(undefined),
  selectedOnboardingFile: createState<string>(''),
  openImportInventory: createState<boolean>(false),
  openImportInvoice: createState<boolean>(false),
  openQuickAdd: createState<boolean>(false),
});

const OnboardingQuery = () => {
  const { studioId, studioName } = useContext(NavigationContext);

  const openDeleteDialog = useState<boolean>(false);
  const search = useState<string>('');
  const sort = useState<SortOnboardingTypes>(undefined);
  const direction = useState<DirectionOnboardingtypes>(undefined);
  const selectedOnboardingFile = useState<string>('');
  const openImportInventory = useState<boolean>(false);
  const openImportInvoice = useState<boolean>(false);
  const openQuickAdd = useState<boolean>(false);

  const equipmentStatus = useState<EquipmentStatus>(
    EquipmentStatus.createEmpty(),
  );

  const { data: filesCount } = useQuery(['onboarding', 'files', 'count'], () =>
    getFilesCount({ studioId }),
  );

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery(
    ['onboarding', 'files', search.get()],
    async ({ pageParam = 1 }) => {
      if (search.get()) {
        return getSearchFiles({
          studioId,
          perPage: 20,
          page: pageParam,
          search: search.get(),
          status: 'open',
        });
      }

      return getFilesByStudio({
        studioId,
        perPage: 20,
        page: pageParam,
        search: search.get(),
        status: 'open',
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        !lastPage.isLastPage ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: 5000,
      onSuccess: results => {
        if (search.get()) {
          searchFiles(
            studioName,
            search.get(),
            results.pages[0].totalAssets > 0,
          );
        }
      },
    },
  );

  return (
    <OnboardingContext.Provider
      value={{
        equipmentStatus: equipmentStatus.get(),
        openDelete: openDeleteDialog,
        search,
        sort,
        direction,
        selectedOnboardingFile,
        openImportInventory,
        openImportInvoice,
        openQuickAdd,
        filesCount,
      }}
    >
      <OnboardingRaw
        filesQuery={data}
        fetchNextPage={fetchNextPage}
        isError={isError}
        isLoading={isLoading}
      />
    </OnboardingContext.Provider>
  );
};

export { OnboardingQuery };
